import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useChartNames } from '@/composables/useChartNames';
import { INVALID_CHART_RECID, isValidChartRecid } from '@/composables/isValidChartRecid';
import { isValidChartListListNum } from '@/composables/isValidChartListListNum';
import { isValidChartListRecid } from '@/composables/isValidChartListRecid';
import { fetchChart } from '@/composables/fetchChart';
import { parseCharts } from '@/composables/parseCharts';
import { refreshChartLists } from '@/composables/refreshChartLists';
import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
import { clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewedIfSymbolHasChanged } from '@/composables/clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewedIfSymbolHasChanged';
import { clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewed } from '@/composables/clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewed';
import { getChartListFromCache } from '@/composables/getChartListFromCache';
import { SimpleFrontendChart } from '@/interfaces/chart';
import { ChartList } from '@/interfaces/chartList';

async function getChartList(listNum?: number, isInitialAttempt: boolean = true): Promise<ChartList | null> {
  let chartList = getChartListFromCache(listNum);

  if (chartList) {
    return chartList;
  }

  await refreshChartLists();

  chartList = getChartListFromCache(listNum);

  if (chartList || !isInitialAttempt) {
    return chartList;
  }

  return await getChartList(listNum, false);
}

function getChartFromCache(recid: number, chartListListNum?: number, chartListRecid?: number): SimpleFrontendChart | null {
  const savedChartsStore = useSavedChartsStore();
  const isListNumValid = typeof chartListListNum === 'number' && isValidChartListListNum(chartListListNum);
  const isRecidValid = typeof chartListRecid === 'number' && isValidChartListRecid(chartListRecid);

  if (!isListNumValid && !isRecidValid) {
    for (const chartList of savedChartsStore.chartLists) {
      for (const chart of chartList.charts) {
        if (chart.recid === recid) {
          return chart;
        }
      }
    }

    return null;
  }

  const cachedChartList = getChartListFromCache(chartListListNum, chartListRecid);

  if (!cachedChartList) {
    return null;
  }

  return cachedChartList.charts.find(c => c.recid === recid) ?? null;
}

async function getChartFromChartList(recid: number, chartListListNum: number, chartListRecid: number, isInitialAttempt = true): Promise<SimpleFrontendChart | null>  {
  await loadChartsForChartList(chartListRecid, undefined, true);

  const chart = getChartFromCache(recid, chartListListNum, chartListRecid);

  if (chart || !isInitialAttempt) {
    return chart;
  }

  return await getChartFromChartList(recid, chartListListNum, chartListRecid, false);
}

export async function getChart(recid: number | string, forceUpdate: boolean = false): Promise<SimpleFrontendChart | null> {
  const savedChartsStore = useSavedChartsStore();
  const { getChartNameAndTemporaryName } = useChartNames();

  if (
    (typeof recid !== 'number' && typeof recid !== 'string') ||
    !isValidChartRecid(recid)
  ) {
    return null;
  }

  const formattedRecid = Number(recid);

  if (!forceUpdate) {
    const cachedChart = getChartFromCache(formattedRecid);

    if (cachedChart) {
      return cachedChart;
    }
  }

  let newlyFetchedChart: SimpleFrontendChart | null = null;

  try {
    const rawChart = await fetchChart(formattedRecid.toString());
    [newlyFetchedChart] = parseCharts([rawChart]);

    if (!newlyFetchedChart) {
      throw new Error(`Unable to parse chart from getChart with recid: ${formattedRecid}.`);
    }

    clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewedIfSymbolHasChanged(newlyFetchedChart);

    const { name, temporaryName } = getChartNameAndTemporaryName(newlyFetchedChart);

    newlyFetchedChart.name = name;
    newlyFetchedChart.temporaryName = temporaryName;
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to get chart with recid: ${formattedRecid} - error occurred while fetching/parsing chart.`, e);

    return null;
  }

  const listNumOfChartListContainingChart = newlyFetchedChart.chartListListNum;

  let chartListContainingChart = getChartListFromCache(listNumOfChartListContainingChart);

  if (!chartListContainingChart) {
    chartListContainingChart = await getChartList(listNumOfChartListContainingChart);
  }

  if (!chartListContainingChart) {
    window.sc3WorkbenchLogger.error(`Failed to get chart with recid: ${formattedRecid} - unable to get chartList with chartListListNum: ${listNumOfChartListContainingChart}.`);

    savedChartsStore.$patch((state) => {
      for (const chartList of state.chartLists) {
        chartList.charts = chartList.charts.filter(c => c.recid !== formattedRecid);
      }

      if (state.recidOfChartBeingPreviewedInChartLibraryListView === formattedRecid) {
        state.recidOfChartBeingPreviewedInChartLibraryListView = INVALID_CHART_RECID;
      }

      if (state.recidOfChartBeingDeletedInModalView === formattedRecid) {
        state.recidOfChartBeingDeletedInModalView = INVALID_CHART_RECID;
      }

      if (state.recidOfChartBeingEditedInModalView === formattedRecid) {
        state.recidOfChartBeingEditedInModalView = INVALID_CHART_RECID;
      }
    });

    clearRecidsOfChartAndLoadSymbolOnlyChartBeingViewed(formattedRecid);

    return null;
  }

  const recidOfChartListContainingChart = chartListContainingChart.recid;

  savedChartsStore.$patch((state) => {
    for (const chartList of state.chartLists) {
      const shouldChartListContainChart = chartList.listNum === listNumOfChartListContainingChart && chartList.recid === recidOfChartListContainingChart;

      if (!shouldChartListContainChart) {
        chartList.charts = chartList.charts.filter(c => c.recid !== formattedRecid);
        continue;
      }

      const indexOfChart = chartList.charts.findIndex(c => c.recid === formattedRecid);

      if (indexOfChart > -1) {
        chartList.charts.splice(indexOfChart, 1, newlyFetchedChart);
      }
    }
  });

  let chart = getChartFromCache(formattedRecid, listNumOfChartListContainingChart, recidOfChartListContainingChart);

  if (chart) {
    return chart;
  }

  chart = await getChartFromChartList(formattedRecid, listNumOfChartListContainingChart, recidOfChartListContainingChart);

  if (!chart) {
    window.sc3WorkbenchLogger.error(`Failed to get chart with recid: ${formattedRecid} - unable to get chart in chartList with chartListListNum: ${listNumOfChartListContainingChart} and chartListRecid: ${recidOfChartListContainingChart}.`);
  }

  return chart;
}
